<template>
  <a-layout class="user-notifications-list">
    <TitleBreadcrumb />
    <div class="content-base container" ref="prueba0">
      <a-card class="user-notifications-list-card">
        <a-row
          type="flex"
          justify="end"
          :class="{ 'flex-direction-reverse': isMobile }"
          :gutter="[16, 16]"
        >
          <a-col :span="24">
            <a-input-search
              :placeholder="labels.placeholderSearch"
              style="width: 100%"
              @search="handleSearch"
              allowClear
            />
          </a-col>
        </a-row>
        <a-divider
          orientation="center"
          type="horizontal"
          :style="{ marginBottom: 0 }"
        />
        <a-row type="flex" justify="center" :gutter="[16, 16]">
          <a-col :xs="24">
            <StandardTable
              ref="standardTable"
              :data="getUserNotifications"
              :columns="columns"
              :locale="locale"
              :handleShowTotal="handleShowTotal"
              :scroll="{ x: true }"
              :key="key"
            />
          </a-col>
        </a-row>
      </a-card>
    </div>
  </a-layout>
</template>
<script>
import { mapState, mapGetters, mapActions } from "vuex";
import TitleBreadcrumb from "@/components/layout/client/TitleBreadcrumb.vue";
import loadingMixin from "@/mixins/loadingMixin";
import { pageTitle } from "@/utils/utils";
import labels from "@/utils/labels";
import StandardTable from "@/components/core/table/StandardTable.vue";
import { DATE_FORMAT } from "../../utils/utils";
import {
  updateUserNotification,
  deleteUserNotification,
} from "@/notifications/fcm-messaging";

export default {
  components: { TitleBreadcrumb, StandardTable },
  name: "UserNotificationsList",
  mixins: [loadingMixin],
  metaInfo: {
    title: pageTitle(labels.userNotificationsList.subtitle),
    meta: [
      {
        name: "title",
        content: labels.appName,
      },
      {
        name: "description",
        content: labels.userNotificationsList.subtitle,
      },
    ],
  },
  data() {
    return {
      key: 0,
      labels: labels.userNotificationsList,
      search: "",
      activeRow: null,
      showid: null,
      nuuid: null,
    };
  },
  async mounted() {
    this.handleRouteChange(this.$route);
  },
  methods: {
    ...mapActions("notifications", ["refreshUserNotifications"]),
    handleReloadTable() {
      let filtersTable = this.$refs.standardTable.filters;
      let result = { ...filtersTable };
      this.$refs.standardTable.handleFilters(result);
    },
    handleShowTotal(total, range) {
      return `${range[0]}-${range[1]} de ${total}`;
    },
    handleSearch(search) {
      this.$refs.standardTable.handleSearch(search);
    },
    handleRouteChange(route) {
      if (route.params.uuid) {
        const notification = this.getUserNotifications.find(
          (item) => item.uuid === route.params.uuid
        );
        if (notification?.uuid) {
          this.showid = notification.uuid;
          this.handleShowMoreInfo(notification);
        }
      }
    },
    handleShowMoreInfo(record, event) {
      event?.stopPropagation();
      this.$info({
        title: this.labels.moreInfo.mainTitle.replace(
          "{code}",
          record?.code || ""
        ),
        class: "notification-more-info-modal",
        content: (h) =>
          h("div", null, [
            h("p", null, [
              h("strong", null, this.labels.moreInfo.date + ":"),
              " " +
                this.$moment(record?.date).format(DATE_FORMAT.MOMENT_DATE_TIME),
            ]),
            h("p", null, [
              h("strong", null, this.labels.moreInfo.topic + ":"),
              " " + (record?.event || record?.channel || "-"),
            ]),
            h("p", null, [
              h("strong", null, this.labels.moreInfo.subject + ":"),
              " " + record?.body,
            ]),
            h("p", null, [
              h("strong", null, this.labels.moreInfo.message + ":"),
              h("div", {
                domProps: {
                  innerHTML: record?.data?.message || "-",
                },
              }),
            ]),
          ]),
      });
      this.handleUpdate({ uuid: record.uuid, read: true });
    },
    handleDelete(record, event) {
      event?.stopPropagation();
      this.$confirm({
        title: this.labels.actions.delete.title,
        content: this.labels.actions.delete.content,
        okText: this.labels.actions.delete.okText,
        okType: "danger",
        cancelText: this.labels.actions.delete.cancelText,
        onOk: async () => {
          try {
            this.loading = true;
            await deleteUserNotification({ uuid: record.uuid });
            this.key++;
            this.$refs.standardTable?.handleRequest();
          } catch (error) {
            console.error(error);
          } finally {
            this.loading = false;
          }
        },
      });
    },
    handleRead(record, event) {
      event?.stopPropagation();
      this.handleUpdate({
        uuid: record.uuid,
        read: !record.read,
      });
      this.key++;
      this.$refs.standardTable?.handleRequest();
    },
    async handleUpdate(record) {
      await updateUserNotification({
        uuid: record.uuid,
        read: record.read,
      });
      this.key++;
      this.$refs.standardTable?.handleRequest();
    },
  },
  computed: {
    ...mapState("layout", ["screenWidth"]),
    ...mapGetters("notifications", ["getUserNotifications"]),
    locale() {
      return {
        emptyText: this.labels.table.emptyText,
      };
    },
    columns() {
      return [
        {
          title: this.labels.table.columns.read,
          dataIndex: "read",
          key: "read",
          sorter: true,
          customRender: (_, record) => {
            return (
              <div class="action-buttons">
                <a-button
                  class="more-info-button fas"
                  title={this.labels.table.columns.read}
                  onClick={(event) => this.handleRead(record, event)}
                >
                  <font-awesome-icon
                    class="anticon"
                    icon={["fas", record.read ? "envelope-open" : "envelope"]}
                  />
                </a-button>
              </div>
            );
          },
        },
        {
          title: this.labels.table.columns.date,
          dataIndex: "date",
          key: "date",
          sorter: true,
          customRender: (text) => {
            return this.$moment(text).format(DATE_FORMAT.MOMENT_DATE_TIME);
          },
        },
        {
          title: this.labels.table.columns.subject,
          dataIndex: "subject",
          key: "subject",
          sorter: true,
          customRender: (_, record) => {
            return record.body || "-";
          },
        },
        {
          title: this.labels.table.columns.action,
          dataIndex: "action",
          key: "action",
          width: "120px",
          align: "center",
          customRender: (_, record) => {
            return (
              <div
                class="action-buttons"
                ref="prueba"
                uuid={"p-" + record.uuid}
              >
                <a-button-group>
                  <a-button
                    type="primary"
                    icon="eye"
                    class="more-info-button"
                    title={this.labels.actions.moreInfo}
                    onClick={(event) => this.handleShowMoreInfo(record, event)}
                    ref={"action-" + record.uuid}
                  />
                  <a-button
                    type="danger"
                    icon="delete"
                    title={this.labels.actions.moreInfo}
                    onClick={(event) => this.handleDelete(record, event)}
                  />
                </a-button-group>
              </div>
            );
          },
        },
      ].filter((column) => !column?.hidden);
    },
  },
};
</script>
<style lang="scss">
@import "~@/assets/scss/variable";
.user-notifications-list {
  max-width: 100% !important;
  font-family: "Roboto", sans-serif;
  background-color: transparent;
  @media screen and (max-width: $screen-mobile) {
    max-width: 100% !important;
  }
  .content-base {
    padding-top: 60px;
    @media screen and (max-width: $screen-mobile) {
      max-width: 100% !important;
      padding-top: 14px;
    }
  }
  &-card {
    background-color: var(--card-shop-bg);
    border-color: var(--card-shop-shadow);
    padding: 1.125rem;
    border-radius: 8px;
    @media screen and (max-width: $screen-mobile) {
      padding: 2.125rem 1rem;
      width: 100%;
    }
    .ant-input {
      border: 1px solid var(--text-muted) !important;
      color: var(--text) !important;
      font-family: "Exo 2", sans-serif !important;
      background-color: transparent;
    }
    .ant-input-search-icon {
      color: var(--text) !important;
      transition: all 0.5s ease;
      :hover {
        color: var(--text-active) !important;
        transition: all 0.5s ease;
      }
    }
    .ant-input-clear-icon {
      color: var(--text) !important;
      transition: all 0.5s ease;
      :hover {
        color: var(--danger) !important;
        transition: all 0.5s ease;
      }
    }
    .ant-table {
      &:hover {
        transition: 0.5s ease;
        background: unset;
      }
      tr.ant-table-row:hover {
        transition: 0.5s ease;
        background: var(--table-hover) !important;
      }
      tr:nth-child(2n) {
        transition: 0.5s ease;
        background: var(--table-striped) !important;
      }
      tr.table-row-disabled,
      tr.table-row-disabled:hover {
        color: var(--table-disabled) !important;
        background: var(--table-disabled) !important;
        font-weight: 600;
      }
      .ant-table-tbody > tr.ant-table-row:hover > td {
        transition: 0.5s ease;
        background: none !important;
      }
      &-tbody,
      &-thead {
        color: var(--text) !important;
        tr {
          th {
            color: var(--text) !important;
            background-color: transparent !important;
          }
        }
      }
      &-footer {
        background: transparent;
      }
      .action-button-view {
        color: var(--btn-text-secondary);
        border-color: var(--btn-bc-secondary);
        background-color: var(--btn-bg-secondary);
        transition: all 0.5s ease;
        &:hover {
          transition: all 0.5s ease;
          color: var(--btn-text-secondary);
          border-color: var(--btn-bc-secondary);
          background-color: var(--btn-bg-secondary);
        }
      }
      .ant-table-placeholder {
        background: var(--card-bg);
        color: var(--text);
        border-radius: 0;
      }
      @media screen and (max-width: $screen-mobile) {
        &-small {
          border: none;
        }
        &-body {
          margin: 0 1px;
        }
      }
    }
    .ant-pagination-prev .ant-pagination-item-link,
    .ant-pagination-next .ant-pagination-item-link {
      color: var(--btn-text-secondary) !important;
      background-color: var(--btn-bg-secondary) !important;
      border-color: var(--btn-bc-secondary) !important;
    }
    .ant-table-pagination.mini {
      .ant-pagination-prev .ant-pagination-item-link,
      .ant-pagination-next .ant-pagination-item-link {
        background-color: transparent !important;
        border-color: none !important;
        color: var(--text) !important;
      }
    }
    .ant-pagination-item {
      color: var(--btn-text-secondary) !important;
      background-color: var(--btn-bg-secondary) !important;
      border-color: var(--btn-bc-secondary) !important;
      a {
        color: var(--btn-text-secondary) !important;
      }
      &-active {
        color: var(--btn-text-primary) !important;
        background-color: var(--btn-bg-primary) !important;
        border-color: var(--btn-bc-primary) !important;
        a {
          color: var(--btn-text-primary) !important;
        }
      }
    }
    .ant-pagination {
      color: var(--text) !important;
    }
    .btn-new-user-notifications {
      color: var(--btn-text-secondary);
      border-color: var(--btn-bc-secondary);
      background-color: var(--btn-bg-secondary);
      transition: all 0.5s ease;
      &:hover {
        transition: all 0.5s ease;
        color: var(--btn-text-secondary);
        border-color: var(--btn-bc-secondary);
        background-color: var(--btn-bg-secondary);
      }
    }
    .flex-direction-reverse {
      flex-direction: column-reverse;
    }
  }
  .text-danger {
    color: var(--danger) !important;
  }
  .list-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    &-head {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      gap: 6px;
      &-header {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2px;
        max-width: 22%;
        border-right: 4px solid color(border);
        .success {
          color: var(--success);
        }
        .warning {
          color: var(--warning);
        }
        .danger {
          color: var(--danger);
        }
        span {
          font-size: 14px;
          font-weight: 600;
        }
        small {
          font-size: 12px;
          font-weight: 400;
          padding-right: 4px;
        }
      }
      &-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        gap: 2px;
        max-width: 53%;
        overflow: hidden;
      }
      &-footer {
        max-width: 25%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 2px;
      }
    }
  }

  .description-paragraph {
    width: 100%;
    * {
      color: var(--text) !important;
    }
  }
}
</style>
